import {
  AirPriceFreezePurchaseState,
  type AirPriceFreezePurchaseStateEnum,
} from "@hopper-b2b/checkout";
import type * as H from "history";
import queryStringParser from "query-string";

import type { StateValue, send as xstateSend } from "xstate";
import config from "../../utils/config";
import { Event } from "./events";
import { CheckoutQueryParams } from "./types";

/**
 *  Stringifies first StateValue to add to url params
 * @example
 * stringifyParentStateValue({ passengerInfo: { picker: "idle" } });
 * returns "passengerInfo"
 */
export const stringifyParentStateValue = (state: StateValue) => {
  if (typeof state === "string") return state;
  else if (typeof state === "object") {
    const key = Object.keys(state)[0];
    return key;
  } else {
    return "";
  }
};

const untrackedStates = [
  AirPriceFreezePurchaseState.cartQuote,
  AirPriceFreezePurchaseState.payment,
];

//Replace instead of push from these states.
const replaceStates = [AirPriceFreezePurchaseState.cartFulfill];

export const populateCheckoutQueryParams = (
  state: StateValue,
  history: H.History
) => {
  const queryString = history?.location?.search || "";
  const parsedQuery = queryStringParser.parse(queryString);
  const stateQueryParam = parsedQuery?.[CheckoutQueryParams.checkoutState];

  const stringifiedNewState = stringifyParentStateValue(state);

  const shouldUpdateQuery = !untrackedStates.includes(
    stringifiedNewState as AirPriceFreezePurchaseStateEnum
  );

  const shouldReplaceCheckoutStateParam = replaceStates.includes(
    stateQueryParam as AirPriceFreezePurchaseStateEnum
  );

  //  If xstate state value is not in query params or if it conflicts with what the
  //  current query params contains, then we push new query params to the URL

  const historyData = {
    pathname: history.location.pathname,
    search: queryStringParser.stringify({
      ...parsedQuery,
      [CheckoutQueryParams.checkoutState]: stringifiedNewState,
    }),
    state: history.location.state,
  };

  if (!stateQueryParam) {
    history.replace(historyData);
  } else if (
    stateQueryParam &&
    stringifiedNewState !== stateQueryParam &&
    shouldUpdateQuery
  ) {
    const historyOperation = shouldReplaceCheckoutStateParam
      ? history.replace
      : history.push;
    historyOperation(historyData);
  }
};

export const mapFlightStateToTransitionEvent = {
  [AirPriceFreezePurchaseState.priceFreezeOffer]:
    Event.GO_TO_PRICE_FREEZE_OFFER,
  [AirPriceFreezePurchaseState.confirmation]:
    Event.GO_TO_PRICE_FREEZE_CONFIRMATION,
  [AirPriceFreezePurchaseState.payment]: Event.GO_TO_PAYMENT,
  [AirPriceFreezePurchaseState.cartQuote]: Event.GO_TO_FULLFILL,
  [AirPriceFreezePurchaseState.cartFulfill]: Event.GO_TO_FULLFILL_FAILED,
};

export const transitionStateOnPathnameChange = (
  state: StateValue,
  history: H.History,
  send: typeof xstateSend
) => {
  const queryString = history.location.search;
  const parsedQuery = queryStringParser.parse(queryString);
  const stateQueryParam = parsedQuery?.[CheckoutQueryParams.checkoutState];

  const stringifiedNewState = stringifyParentStateValue(state);

  const allowTransition =
    stateQueryParam &&
    !untrackedStates.includes(
      stateQueryParam as AirPriceFreezePurchaseStateEnum
    );

  //End states
  const hasFailedFulfillment =
    stringifiedNewState === AirPriceFreezePurchaseState.fulfillFailed;
  const hasSucceededFulfillment =
    stringifiedNewState === AirPriceFreezePurchaseState.confirmation;

  if (
    stateQueryParam &&
    stringifiedNewState !== stateQueryParam &&
    allowTransition
  ) {
    if (hasSucceededFulfillment) {
      //Handles when user clicks back on browser navigation and sends user to PF list.
      handleFulfillSuccess(history);
    } else if (hasFailedFulfillment) {
      handleFulfillError(send);
    } else {
      send({
        type: mapFlightStateToTransitionEvent[
          stateQueryParam as AirPriceFreezePurchaseStateEnum
        ],
      });
    }
  }
};

export const handleFulfillError = (send: typeof xstateSend) => {
  send({
    type: mapFlightStateToTransitionEvent[
      AirPriceFreezePurchaseState.priceFreezeOffer
    ],
  });
};

export const handleFulfillSuccess = (history: H.History) => {
  window.parent.postMessage(
    {
      type: "FINISH_SESSION",
      payload: null,
    },
    config.UBER_URL
  );
};
